import {
  FavoriteInspiration,
  Inspiration,
  InspirationSummary,
} from "@/types/inspiration"
import { createSelector } from "@reduxjs/toolkit"
import uniqBy from "lodash/uniqBy"

import { now } from "@/lib/date-utils"
import { DEFAULT_PLACES_PER_PAGE, PagingMeta } from "@/lib/paging"
import { getConstantEmptySet } from "@/lib/utils/collections"
import { RootState } from "../redux-store"
import { selectCurrentUserRef } from "../user-slice"
import { baseApi } from "./base-api"

export interface GetSavedGuidesResponse {
  data: InspirationSummary[]
  meta: PagingMeta
}

export interface GetSavedGuidesArgs {
  page?: number
  perPage?: number
  q?: string
}

export const savedInspirationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSavedGuides: builder.query<GetSavedGuidesResponse, GetSavedGuidesArgs>({
      query: (args) => {
        const params = new URLSearchParams()
        const page = args.page ?? 1
        const perPage = args.perPage ?? DEFAULT_PLACES_PER_PAGE
        params.append("page", page.toString())
        params.append("perPage", perPage.toString())
        if (args.q) params.append("q", args.q)
        return {
          url: `/api/favorite_inspirations/inspirations?${params.toString()}`,
        }
      },
      providesTags: ["SavedGuides"],
      // One cache entry per search, shared by all of its pages; the unfiltered
      // entry feeds selectSavedGuidesCount.
      serializeQueryArgs: ({ queryArgs }) => `savedGuides:${queryArgs.q ?? ""}`,
      merge: (current, incoming, { arg }) => {
        // Handle pagination merge for append behavior
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            // A tag refetch repeats the last page requested, so drop repeats.
            data: uniqBy([...current.data, ...incoming.data], "entity_ref"),
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
      keepUnusedDataFor: Infinity,
    }),
    getSavedGuidesList: builder.query<FavoriteInspiration[], void>({
      query: (_arg) => ({
        url: "/api/favorite_inspirations",
      }),
      transformResponse: (response: {
        favorite_inspirations: FavoriteInspiration[]
      }) => response.favorite_inspirations,
      providesTags: ["SavedGuides"],
      keepUnusedDataFor: Infinity,
    }),
    addSavedGuide: builder.mutation<FavoriteInspiration, Inspiration>({
      query: (inspiration) => ({
        url: "/api/favorite_inspirations",
        method: "POST",
        data: { entity_ref: inspiration.entity_ref },
      }),
      transformResponse: (response: {
        favorite_inspiration: FavoriteInspiration
      }) => response.favorite_inspiration,
      invalidatesTags: ["SavedGuides"],
      async onQueryStarted(inspiration, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          savedInspirationsApi.util.updateQueryData(
            "getSavedGuidesList",
            undefined,
            (draft) => {
              draft.unshift({
                entity_ref: inspiration.entity_ref,
                updated_at: now(),
              })
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    removeSavedGuide: builder.mutation<void, Inspiration>({
      query: (inspiration) => ({
        url: `/api/favorite_inspirations/${inspiration.entity_ref}`,
        method: "DELETE",
      }),
      transformResponse: () => undefined,
      invalidatesTags: ["SavedGuides"],
      async onQueryStarted(inspiration, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          savedInspirationsApi.util.updateQueryData(
            "getSavedGuidesList",
            undefined,
            (draft) => {
              const index = draft.findIndex(
                (g) => g.entity_ref === inspiration.entity_ref
              )
              if (index !== -1) draft.splice(index, 1)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
  }),
})

export const {
  useGetSavedGuidesQuery,
  useLazyGetSavedGuidesQuery,
  useGetSavedGuidesListQuery,
  useLazyGetSavedGuidesListQuery,
  useAddSavedGuideMutation,
  useRemoveSavedGuideMutation,
  usePrefetch,
} = savedInspirationsApi

const selectSavedGuidesResult =
  savedInspirationsApi.endpoints.getSavedGuides.select({})

const selectSavedGuidesListResult =
  savedInspirationsApi.endpoints.getSavedGuidesList.select()

export const selectSavedGuidesList = createSelector(
  selectSavedGuidesListResult,
  (result) => result.data ?? []
)

const selectSavedGuidesListEntityRefs = createSelector(
  [selectSavedGuidesList],
  (savedGuidesList) => {
    return savedGuidesList.length > 0
      ? new Set(savedGuidesList.map((savedGuide) => savedGuide.entity_ref))
      : getConstantEmptySet()
  }
)

const selectSavedGuidesRaw = createSelector(
  selectSavedGuidesResult,
  (result) => result.data?.data ?? []
)

const selectSavedGuides = createSelector(
  [selectSavedGuidesRaw, selectSavedGuidesListEntityRefs, selectCurrentUserRef],
  (guides, savedRefs, currentUserRef) => {
    return guides.filter(
      (guide) =>
        savedRefs.has(guide.entity_ref) ||
        guide.owner.user_ref === currentUserRef
    )
  }
)

export const selectSavedGuidesCount = createSelector(
  [
    selectSavedGuidesResult,
    selectSavedGuidesList,
    selectSavedGuidesRaw,
    selectSavedGuides,
  ],
  (queryResult, savedGuidesList, rawGuides, filteredGuides) => {
    const removedCount = rawGuides.length - filteredGuides.length
    const serverTotal =
      queryResult.data?.meta?.total_count ?? savedGuidesList.length ?? 0
    return Math.max(0, serverTotal - removedCount)
  }
)

export const selectSavedGuidesIsLoading = createSelector(
  selectSavedGuidesResult,
  (result) => result.isLoading
)

export const selectIsSavedGuide = createSelector(
  [
    selectSavedGuidesListEntityRefs,
    (_state: RootState, inspiration: Inspiration) => inspiration,
  ],
  (savedGuidesListEntityRefs, inspiration) => {
    return savedGuidesListEntityRefs.has(inspiration.entity_ref)
  }
)
